class NscProductPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.productPageInitialized === 'true') return;

    this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    this.optionInputs = Array.from(this.querySelectorAll('[data-product-option]'));
    this.variantInput = this.querySelector('[data-product-variant-id]');
    this.price = this.querySelector('[data-product-price]');
    this.availability = this.querySelector('[data-product-availability]');
    this.submitButton = this.querySelector('[data-product-submit]');
    this.submitButtonText = this.querySelector('[data-product-submit-text]');
    this.quantityInput = this.querySelector('[data-product-quantity]');
    this.mainImage = this.querySelector('[data-product-main-image] img');
    this.thumbnails = Array.from(this.querySelectorAll('[data-product-thumbnail]'));

    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => this.activateMedia(thumbnail));
    });
    this.querySelector('[data-product-quantity-decrease]')?.addEventListener('click', () => this.changeQuantity(-1));
    this.querySelector('[data-product-quantity-increase]')?.addEventListener('click', () => this.changeQuantity(1));
    this.quantityInput?.addEventListener('change', () => this.normalizeQuantity());

    this.dataset.productPageInitialized = 'true';
    this.updateOptionAvailability();
  }

  get selectedOptions() {
    const selected = [];
    this.optionInputs.forEach((input) => {
      if (input.checked) selected[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    return selected;
  }

  onOptionChange() {
    const options = this.selectedOptions;
    const variant = this.variants.find((candidate) =>
      candidate.options.every((option, index) => option === options[index])
    );

    this.updateOptionAvailability();

    if (!variant) {
      this.setUnavailable(this.dataset.unavailableLabel);
      return;
    }

    this.variantInput.value = variant.id;
    this.price.textContent = variant.price;
    this.updateQuantityRules(variant);

    this.variantInput.disabled = !variant.available;
    this.submitButton.disabled = !variant.available;
    this.submitButton.setAttribute('aria-disabled', String(!variant.available));
    this.availability.hidden = variant.available;
    this.availability.textContent = variant.available ? '' : this.dataset.soldOutLabel;
    this.submitButtonText.textContent = variant.available
      ? `${this.dataset.submitLabel} · ${variant.price}`
      : this.dataset.soldOutLabel;

    if (variant.featuredMediaId) {
      const thumbnail = this.thumbnails.find(
        (candidate) => Number(candidate.dataset.mediaId) === variant.featuredMediaId
      );
      if (thumbnail) this.activateMedia(thumbnail);
    }

    if (!window.Shopify?.designMode) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions;

    this.optionInputs.forEach((input) => {
      const candidateOptions = selected.slice();
      candidateOptions[Number(input.dataset.optionPosition) - 1] = input.value;
      const exists = this.variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((option, index) => option === candidateOptions[index])
      );
      input.disabled = !exists && !input.checked;
    });
  }

  setUnavailable(message) {
    this.variantInput.disabled = true;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButtonText.textContent = message;
    this.availability.textContent = message;
    this.availability.hidden = false;
  }

  updateQuantityRules(variant) {
    if (!this.quantityInput) return;

    this.quantityInput.min = variant.quantityMin;
    this.quantityInput.step = variant.quantityIncrement;
    if (variant.quantityMax === null) {
      this.quantityInput.removeAttribute('max');
    } else {
      this.quantityInput.max = variant.quantityMax;
    }
    this.normalizeQuantity();
  }

  changeQuantity(direction) {
    if (!this.quantityInput) return;

    const step = Number(this.quantityInput.step) || 1;
    this.quantityInput.value = Number(this.quantityInput.value) + direction * step;
    this.normalizeQuantity();
  }

  normalizeQuantity() {
    if (!this.quantityInput) return;

    const minimum = Number(this.quantityInput.min) || 1;
    const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const step = Number(this.quantityInput.step) || 1;
    const entered = Number(this.quantityInput.value);
    const value = Number.isFinite(entered) ? entered : minimum;
    const stepped = minimum + Math.round((value - minimum) / step) * step;
    this.quantityInput.value = Math.min(maximum, Math.max(minimum, stepped));
  }

  activateMedia(thumbnail) {
    if (!this.mainImage || !thumbnail.dataset.imageUrl) return;

    this.mainImage.src = thumbnail.dataset.imageUrl;
    this.mainImage.removeAttribute('srcset');
    this.mainImage.alt = thumbnail.dataset.imageAlt || '';
    this.mainImage.width = Number(thumbnail.dataset.imageWidth);
    this.mainImage.height = Number(thumbnail.dataset.imageHeight);
    this.thumbnails.forEach((candidate) => candidate.setAttribute('aria-current', String(candidate === thumbnail)));
  }
}

['nsc-bean-product', 'nsc-instant-product'].forEach((tagName) => {
  if (!customElements.get(tagName)) customElements.define(tagName, class extends NscProductPage {});
});
